import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, first, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { User } from '@datarecs/data-models-core-api';
import { AUTH_ENTRY_ROUTE } from '../auth-entry';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private refreshInFlight$: Observable<void> | null = null;

  private readonly _user = signal<User | null>(null);
  readonly user = this._user.asReadonly();

  readonly isLoggedIn = computed(() => this.user() !== null);

  readonly isAdmin = computed(() => {
    const user = this.user();
    const roles = user?.roles || [];
    return roles.includes('admin') || roles.includes('owner');
  });

  /**
   * The granular permissions granted to the current identity.
   *
   * IMPORTANT: permissions are NOT roles. `view_job_results` is a *permission*;
   * `admin` / `editor` are *roles*. The two only coincided historically (this
   * computed used to return `user.roles`), which silently mis-gated any control
   * whose permission string did not happen to match a role name.
   *
   * The authoritative role->permission resolution lives server-side in core-api's
   * AuthorisationGuard. The client cannot and must not re-derive permissions from
   * roles; it consumes the resolved `permissions` array the API attaches to the
   * authenticated identity. Until the API supplies it, this returns [] and the UI
   * relies on the `isAdmin()` bypass below to keep admins/owners unblocked. Either
   * way, gating here is UX only — the server is the real authority and a 403 from
   * the API is still the final word if a control is shown that shouldn't be.
   */
  readonly userPermissions = computed<string[]>(() => {
    const user = this.user();
    if (!user) return [];
    // `permissions` is the resolved granular-permission list on the authenticated
    // identity (present on ApiKey / OidcConnector identities today, and added to
    // the session User identity by the /auth/me change tracked alongside this fix).
    // It is read defensively because the vendored `User` type may not declare it
    // yet; never fall back to `roles`, which are not permissions.
    return (user as { permissions?: string[] }).permissions ?? [];
  });

  redirectToLogin(): void {
    // Tenant-first login: send unauthenticated users to the pre-auth entry route
    // (the tenant picker) rather than the legacy email-first login page.
    this.router.navigate([AUTH_ENTRY_ROUTE]);
  }

  loadUser(): void {
    this.http.get<User>(`${this.apiUrl}/auth/me`, { withCredentials: true })
      .pipe(first())
      .subscribe({
        next: user => this._user.set(user),
        error: () => this._user.set(null),
      });
  }

  refreshSession(): Observable<void> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    this.refreshInFlight$ = this.http.post<void>(`${this.apiUrl}/auth/refresh`, {}, { withCredentials: true }).pipe(
      tap(() => this.loadUser()),
      catchError(err => {
        this._user.set(null);
        return throwError(() => err);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.refreshInFlight$;
  }

  logout(): void {
    this.http.post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(first())
      .subscribe(async () => {
        this._user.set(null);
        await this.router.navigate([AUTH_ENTRY_ROUTE]);
      });
  }

  logoutAll(): void {
    this.http.post(`${this.apiUrl}/auth/logout-all`, {}, { withCredentials: true })
      .pipe(first())
      .subscribe(async () => {
        this._user.set(null);
        await this.router.navigate([AUTH_ENTRY_ROUTE]);
      });
  }
}
